import { FC, useCallback, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  giftOrderAnalysisDataAtomFamily,
  giftOrderAnalysisStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import {
  GiftOrderListDateRange,
  useGiftOrderAnalysisHook,
} from '@shared/api/giftOrder/giftOrder.hook';
import { Loading } from '@shared/components/Loading';

import { ChannelTab } from '../containers/GiftOrderAnalysis/ChannelTab';
import { DeliveryStatsTab } from '../containers/GiftOrderAnalysis/DeliveryStatsTab';
import { LaterTab } from '../containers/GiftOrderAnalysis/LaterTab';
import { TimingTab } from '../containers/GiftOrderAnalysis/TimingTab';
import {
  AnalysisTab,
  DATE_PRESET_LABELS,
  DatePreset,
  TABS,
  getDatePreset,
} from '../containers/GiftOrderAnalysis/analysisUtils';

export const GiftOrderAnalysisPage: FC = function GiftOrderAnalysisPage() {
  const [tab, setTab] = useState<AnalysisTab>('timing');
  const [datePreset, setDatePreset] = useState<DatePreset>('1w');
  const [dateRange, setDateRange] = useState<GiftOrderListDateRange>(
    getDatePreset('1w'),
  );
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const filterKey = `analysis_${dateRange.from ?? 'all'}_${dateRange.to ?? 'all'}`;

  useGiftOrderAnalysisHook(filterKey, false, dateRange);
  const status = useRecoilValue(giftOrderAnalysisStatusAtomFamily(filterKey));
  const orderList = useRecoilValue(
    giftOrderAnalysisDataAtomFamily(filterKey),
  );

  const handleDatePreset = useCallback((preset: DatePreset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      setDateRange(getDatePreset(preset));
    }
  }, []);

  const handleCustomDateApply = useCallback(() => {
    if (customFrom || customTo) {
      setDateRange({
        from: customFrom || undefined,
        to: customTo || undefined,
      });
    }
  }, [customFrom, customTo]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">데이터 분석</h1>
        <p className="mt-1 text-sm text-gray-500">
          발인일 기준 답례품 구매 시점 · 매출 · 발송 채널 분석
        </p>
      </div>

      {/* 탭 */}
      <div className="mb-4 border-b border-gray-200">
        <div className="flex gap-0">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`border-b-2 px-5 py-2.5 text-sm font-medium transition-colors ${
                tab === t.key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 기간 필터 */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500">기간</span>
        <div className="flex gap-1">
          {(Object.keys(DATE_PRESET_LABELS) as DatePreset[])
            .filter((k) => k !== 'custom')
            .map((preset) => (
              <button
                key={preset}
                onClick={() => handleDatePreset(preset)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                  datePreset === preset
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {DATE_PRESET_LABELS[preset]}
              </button>
            ))}
          <button
            onClick={() => handleDatePreset('custom')}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              datePreset === 'custom'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            직접선택
          </button>
        </div>
        {datePreset === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
            />
            <span className="text-xs text-gray-400">~</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
            />
            <button
              onClick={handleCustomDateApply}
              disabled={!customFrom && !customTo}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              조회
            </button>
          </div>
        )}
        {dateRange.from && (
          <span className="text-xs text-gray-400">
            {dateRange.from} ~ {dateRange.to ?? '현재'}
          </span>
        )}
      </div>

      {tab === 'timing' && <TimingTab orderList={orderList} />}
      {tab === 'channel' && <ChannelTab orderList={orderList} />}
      {tab === 'later' && <LaterTab orderList={orderList} />}
      {/* 발송 통계는 무거울 수 있어 탭이 열릴 때만 자체 조회 (마운트 시 fetch) */}
      {tab === 'delivery' && (
        <DeliveryStatsTab from={dateRange.from} to={dateRange.to} />
      )}
    </div>
  );
};
